import { collection, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { logError, logInfo } from '../logger';

// Collection name for storing WFS layers
const WFS_LAYERS_COLLECTION = 'wfs_layers';

// Layer definitions with direct GeoJSON URLs
const LAYER_DEFINITIONS = [
  {
    layerId: 'ms:aq_camion',
    name: 'Réseau de camionnage',
    description: 'Réseau de camionnage autorisé',
    color: '#FBBF24', // Yellow
    url: 'https://ws.mapserver.transports.gouv.qc.ca/donnees/geomatique/aq_camion.geojson'
  },
  {
    layerId: 'ms:evenements',
    name: 'Avertissements routiers',
    description: 'Avertissements et travaux routiers',
    color: '#F59E0B', // Amber
    url: 'https://ws.mapserver.transports.gouv.qc.ca/swtq?service=wfs&version=2.0.0&request=getfeature&typename=ms:evenements&outfile=AvertissementRoutier&srsname=EPSG:4326&outputformat=geojson'
  },
  {
    layerId: 'ms:gsq_v_desc_strct_tri',
    name: 'Structures',
    description: 'Structures (ponts, viaducs, etc.)',
    color: '#6366F1', // Indigo
    url: 'https://ws.mapserver.transports.gouv.qc.ca/swtq?service=wfs&version=2.0.0&request=getfeature&typename=ms:gsq_v_desc_strct_tri&outfile=Structure&srsname=EPSG:4326&outputformat=geojson'
  },
  {
    layerId: 'ms:iit_v_drang_condu_permn_ligne',
    name: 'Élément de drainage',
    description: 'Éléments de drainage du réseau routier',
    color: '#14B8A6', // Teal
    url: 'https://ws.mapserver.transports.gouv.qc.ca/swtq?service=wfs&version=2.0.0&request=getfeature&typename=ms:iit_v_drang_condu_permn_ligne&outfile=ElementDrainage&srsname=EPSG:4326&outputformat=geojson'
  },
  {
    layerId: 'ms:pes_v_limtn_charg',
    name: 'Limites de charge',
    description: 'Limites de charge sur les routes',
    color: '#EF4444', // Red
    url: 'https://ws.mapserver.transports.gouv.qc.ca/swtq?service=wfs&version=2.0.0&request=getfeature&typename=ms:pes_v_limtn_charg&outfile=LimiteCharge&srsname=EPSG:4326&outputformat=geojson'
  }
];

/**
 * Fetches a GeoJSON file from a URL
 * @param {Object} layer - Layer definition
 * @returns {Promise<Object>} - GeoJSON data
 */
export const fetchGeoJSON = async (layer) => {
  try {
    logInfo(`Fetching GeoJSON for ${layer.name} from ${layer.url}`);
    
    // Add browser-like headers to avoid CORS issues
    const headers = {
      'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
      'Accept': 'application/json, text/plain, */*',
      'Accept-Language': 'en-US,en;q=0.9,fr-CA;q=0.8,fr;q=0.7',
      'Referer': 'https://www.transports.gouv.qc.ca/',
      'Origin': 'https://www.transports.gouv.qc.ca'
    };

    // Fetch with retry logic
    const response = await fetchWithRetry(layer.url, headers);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch GeoJSON: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Validate GeoJSON structure
    if (!data.type || !data.features) {
      throw new Error('Invalid GeoJSON format');
    }
    
    logInfo(`Successfully fetched ${data.features.length} features for ${layer.name}`);
    return data;
  } catch (error) {
    logError(`Failed to fetch GeoJSON for ${layer.name}:`, error);
    throw error;
  }
};

/**
 * Fetch with retry logic
 * @param {string} url - URL to fetch
 * @param {Object} headers - Request headers
 * @param {number} retries - Number of retries
 * @returns {Promise<Response>} - Fetch response
 */
const fetchWithRetry = async (url, headers, retries = 3) => {
  let lastError;
  
  for (let i = 0; i < retries; i++) {
    try {
      return await fetch(url, { headers });
    } catch (error) {
      lastError = error;
      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
    }
  }
  
  throw lastError;
};

/**
 * Saves GeoJSON data to Firestore
 * @param {string} layerId - Layer ID
 * @param {Object} data - GeoJSON data
 * @param {string} source - Source URL
 * @returns {Promise<void>}
 */
export const saveGeoJSONToFirestore = async (layerId, data, source) => {
  try {
    const layerRef = doc(db, WFS_LAYERS_COLLECTION, layerId);
    
    // Get existing document to check if we need to update
    const existingDoc = await getDoc(layerRef);
    const now = new Date().toISOString();
    
    if (existingDoc.exists()) {
      const existingData = existingDoc.data();
      const existingFeatureCount = existingData.features?.length || 0;
      const newFeatureCount = data.features?.length || 0;
      
      // Only update if feature count changed or it's been more than a day
      const lastUpdated = new Date(existingData.updatedAt || 0);
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
      
      if (existingFeatureCount !== newFeatureCount || lastUpdated < oneDayAgo) {
        await setDoc(layerRef, {
          layerId,
          features: data.features || [],
          featureCount: data.features?.length || 0,
          source,
          updatedAt: now
        });
        
        logInfo(`Updated layer ${layerId} in Firestore with ${data.features?.length || 0} features`);
      } else {
        logInfo(`Skipped update for layer ${layerId} - no changes detected`);
      }
    } else {
      // New document
      await setDoc(layerRef, {
        layerId,
        features: data.features || [],
        featureCount: data.features?.length || 0,
        source,
        createdAt: now,
        updatedAt: now
      });
      
      logInfo(`Created new layer ${layerId} in Firestore with ${data.features?.length || 0} features`);
    }
  } catch (error) {
    logError(`Failed to save layer ${layerId} to Firestore:`, error);
    throw error;
  }
};

/**
 * Updates all layers from GeoJSON files
 * @returns {Promise<Object>} - Results of the update operation
 */
export const updateAllLayersFromGeoJSON = async () => {
  const results = {
    success: [],
    failed: []
  };

  for (const layer of LAYER_DEFINITIONS) {
    try {
      logInfo(`Processing layer: ${layer.name}`);
      
      // Fetch GeoJSON
      const data = await fetchGeoJSON(layer);
      
      // Save to Firestore
      await saveGeoJSONToFirestore(layer.layerId, data, layer.url);
      
      results.success.push({
        layerId: layer.layerId,
        name: layer.name,
        featuresCount: data.features?.length || 0
      });
      
      logInfo(`Successfully updated layer ${layer.name}`);
    } catch (error) {
      logError(`Failed to update layer ${layer.name}:`, error);
      
      results.failed.push({
        layerId: layer.layerId,
        name: layer.name,
        error: error.message
      });
    }
  }

  // Update metadata
  await updateLayersMetadata();

  return results;
};

/**
 * Updates metadata for WFS layers collection
 * @returns {Promise<void>}
 */
export const updateLayersMetadata = async () => {
  try {
    // Create a metadata document in the collection
    const metadataRef = doc(db, WFS_LAYERS_COLLECTION, 'metadata');
    
    await setDoc(metadataRef, {
      lastFullUpdate: new Date().toISOString(),
      availableLayers: LAYER_DEFINITIONS.map(layer => ({
        id: layer.layerId,
        name: layer.name,
        description: layer.description,
        color: layer.color
      }))
    }, { merge: true });
    
    logInfo('Updated WFS layers metadata');
  } catch (error) {
    logError('Failed to update WFS layers metadata:', error);
    throw error;
  }
};

/**
 * Gets all available layers with their metadata
 * @returns {Promise<Array>} - Array of layer objects
 */
export const getAvailableLayers = async () => {
  try {
    const metadataRef = doc(db, WFS_LAYERS_COLLECTION, 'metadata');
    const metadataDoc = await getDoc(metadataRef);
    
    if (!metadataDoc.exists()) {
      return LAYER_DEFINITIONS.map(layer => ({
        id: layer.layerId,
        name: layer.name,
        description: layer.description,
        color: layer.color,
        isAvailable: false
      }));
    }
    
    const metadata = metadataDoc.data();
    const availableLayers = metadata.availableLayers || [];
    
    // Check which layers are available in Firestore
    const layerAvailability = await Promise.all(
      availableLayers.map(async (layer) => {
        const layerRef = doc(db, WFS_LAYERS_COLLECTION, layer.id);
        const layerDoc = await getDoc(layerRef);
        
        return {
          ...layer,
          isAvailable: layerDoc.exists(),
          updatedAt: layerDoc.exists() ? layerDoc.data().updatedAt : null,
          featureCount: layerDoc.exists() ? layerDoc.data().featureCount : 0
        };
      })
    );
    
    return layerAvailability;
  } catch (error) {
    logError('Failed to get available layers:', error);
    return LAYER_DEFINITIONS.map(layer => ({
      id: layer.layerId,
      name: layer.name,
      description: layer.description,
      color: layer.color,
      isAvailable: false
    }));
  }
};